import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectCallStatus,
  selectCallPhase,
  selectTranscript,
  selectIsSpeaking,
  selectIsAISpeaking,
  selectDuration,
  selectError,
  setUserText,
} from '../../redux/slices/callSlice';
import VoiceWave from './VoiceWave';
import Controls from '../Controls/Controls';
import Transcript from '../Transcript/Transcript';
import './CallPopup.css';

const CallPopup = ({ audioLevel, onEndCall, onToggleMute, onToggleSpeaker, onToggleMic, isMuted, isSpeakerOn, isMicOn, demoMode, micAvailable, onSendText, userText, callError, onClearError }) => {
  const dispatch = useDispatch();
  const status = useSelector(selectCallStatus);
  const phase = useSelector(selectCallPhase);
  const isSpeaking = useSelector(selectIsSpeaking);
  const isAISpeaking = useSelector(selectIsAISpeaking);
  const duration = useSelector(selectDuration);
  const transcript = useSelector(selectTranscript);
  const error = useSelector(selectError);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (!micAvailable && phase === 'connected' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [micAvailable, phase]);

  if (status === 'idle') return null;

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const handleSend = () => {
    const text = inputValue.trim();
    if (!text) return;
    if (onSendText) onSendText(text);
    setInputValue('');
    dispatch(setUserText(''));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSend();
  };

  return (
    <div className="call-popup-overlay animate-fade-in">
      <div className="call-popup glass-card">
        <div className="call-popup-header">
          <div className="call-status">
            {phase === 'ringing' && <span className="ringing-text"><i className="bi bi-telephone-inbound me-2"></i>Incoming Call...</span>}
            {phase === 'connecting' && <span className="connecting-text"><i className="bi bi-arrow-repeat me-2 spin"></i>Connecting...</span>}
            {phase === 'connected' && (
              <>
                <span className={`status-dot ${isAISpeaking ? 'ai-speaking' : isSpeaking ? 'user-speaking' : 'idle'}`}></span>
                <span>{isAISpeaking ? 'AI Speaking' : isSpeaking ? 'You Speaking' : 'Connected'}</span>
              </>
            )}
          </div>
          <div className="call-timer">{formatTime(duration)}</div>
        </div>

        <div className="call-avatar-section">
          <div className={`call-avatar ${isAISpeaking ? 'speaking' : ''}`}>
            <i className="bi bi-robot"></i>
          </div>
          <VoiceWave isActive={isAISpeaking} isAI={true} audioLevel={audioLevel} />
        </div>

        {!micAvailable && phase === 'connected' && (
          <div className="demo-input-section">
            <div className="demo-input-wrapper">
              <input
                ref={inputRef}
                type="text"
                className="form-control demo-text-input"
                placeholder="Type your message..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isAISpeaking}
              />
              <button
                className="btn btn-primary demo-send-btn"
                onClick={handleSend}
                disabled={isAISpeaking || !inputValue.trim()}
              >
                <i className="bi bi-send-fill"></i>
              </button>
            </div>
          </div>
        )}

        <Controls
          isMuted={isMuted}
          isSpeakerOn={isSpeakerOn}
          isMicOn={isMicOn}
          onToggleMute={onToggleMute}
          onToggleSpeaker={onToggleSpeaker}
          onToggleMic={onToggleMic}
          onEndCall={onEndCall}
        />

        {error && (
          <div className="call-error">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        <Transcript />
      </div>
    </div>
  );
};

export default CallPopup;
